/**
 * **页面编辑器的两个侧栏面板** —— 包自己画的（`PanelTabDef.component`）。
 *
 * ```text
 *   左侧「页面」  纸张 / 输出（参数表） + 视图（缩放 / 网格 / 吸附 / 标尺）
 *   右侧「元素」  选中组件的参数（**按参数表分组渲染**） + 实际值读数 + 未接入的组件
 * ```
 *
 * ## 为什么面板要自己写一遍输入框（不复用宿主的面板）
 *
 * `node-packages.md` §4 的 B 路是**明确不做**的：包不往**别人的**窗口里
 * 加东西（节点图 / 属性那些窗口长什么样是内核的事）。
 * 但 `panels` 是**这个编辑器自己的**侧栏 —— 壳归窗口（`NpArea` 统一渲染
 * tab 条），内容归包。所以这里的输入框是包自己的 DOM。
 *
 * ## ★ 字段从**参数表**生成，不是手写的
 *
 * `params.ts` 是参数的单一来源：节点上的行、这里的字段、值域 / 单位 /
 * 标签全从它来。以前两边各写一遍的代价不是打字，而是**它们会漂** ——
 * 节点上写 `min: 100`、面板里写 `min: 10`，两边都不报错，
 * 表现是"画布上拖不动、面板里能填"。
 *
 * ## 两个面板读的是同一份 `doc` 和同一份视图状态
 *
 * 面板和编辑器主体是**两个不相邻的插槽**（宿主分别渲染），靠 props 传值
 * 做不到 —— 共享的东西只有两处：`doc`（数据，宿主给的）和
 * `viewStateOf(area.id)`（视图，包自己的，按窗口 id 索引）。
 * 于是"面板改 mm、编辑器里的框跟着动"是**同一份数据**的必然结果，
 * 不是同步出来的。
 */
import { computed, defineComponent, h, reactive, type PropType, type VNode } from 'vue'
import { NpPanelCard } from '@/components/NpPanelCard'
import { describeJob, normalizeJob, type PrintJob } from './job'
import { describeSimulation, simulateJob } from './jobScript'
import { compileToFile } from './renderTree'
import { NpWidgetImage } from '@/components/widgets/NpWidgetImage'
import type { EditorHostProps, EditorNodeView } from '@/types/editor'
import { pcls, PRINT_PANEL_CLASS } from './classNames'
import { ensurePrintStyles } from './styles'
import {
  activePageOf,
  DOCUMENT_TYPE,
  documentNodeOf,
  documentSeedFrom,
  PAGE_TYPE,
  isPageType,
  nextItemsHandle,
  nextPagesHandle,
  orphanComponents,
  pagesOf,
  type ElementView,
  type PageView,
} from './pageModel'
import { SYMBOLOGIES } from './barcode'
import { BARCODE_DEFAULTS, QRCODE_DEFAULTS } from './nodes'
import { groupsFor, keyParamsFor, paramsFor, type ParamComponent, type ParamSpec } from './params'
import { GRID_CHOICES, viewStateOf } from './viewState'

/* ══════════════════════════════════════════════════════════════════
   共用小件
   ══════════════════════════════════════════════════════════════════ */

/** 一行「标签 + 数值框」—— 面板里所有数值参数都用它 */
function numberField(options: {
  label: string
  action: string
  value: number
  step?: number
  min?: number
  max?: number
  unit?: string
  hint?: string
  onInput: (next: number) => void
}): VNode {
  return h('label', { class: 'npp-field', title: options.hint ?? options.label }, [
    h('span', { class: 'npp-field__label' }, options.label),
    h('input', {
      class: 'npp-input',
      type: 'number',
      'data-npp-field': options.action,
      step: options.step ?? 0.5,
      min: options.min,
      max: options.max,
      value: Math.round(options.value * 100) / 100,
      onInput: (ev: Event) => {
        const raw = Number((ev.target as HTMLInputElement).value)
        /* ⚠️ 非法输入**不写回**（不弹错）：写到文档里的 NaN 会一路传到输出侧 */
        if (Number.isFinite(raw)) options.onInput(raw)
      },
    }),
    options.unit ? h('span', { class: pcls('muted') }, options.unit) : null,
  ])
}

function toggleField(
  label: string,
  action: string,
  value: boolean,
  onToggle: (next: boolean) => void,
  hint?: string,
): VNode {
  return h('label', { class: 'npp-field', title: hint ?? label }, [
    h('span', { class: 'npp-field__label' }, label),
    h('input', {
      type: 'checkbox',
      'data-npp-field': action,
      checked: value,
      onChange: (ev: Event) => onToggle((ev.target as HTMLInputElement).checked),
    }),
    h('span', { class: 'npp-field__gap' }),
  ])
}

function enumField(options: {
  label: string
  action: string
  value: string | number
  choices: readonly { label: string; value: string | number }[]
  hint?: string
  onPick: (next: string | number) => void
}): VNode {
  return h('label', { class: 'npp-field', title: options.hint ?? options.label }, [
    /* ⚠️ `data-npp-read`：把**面板读到的值**摆在 DOM 上（排错用，验证脚本也读它） */
    h('span', { class: 'npp-field__label', 'data-npp-read': String(options.value) }, options.label),
    h(
      'select',
      {
        class: 'npp-input',
        'data-npp-field': options.action,
        /*
         * ⚠️ `key` 跟着值走：**值一变就重建这个 `<select>`**，配合下面的 `ref`
         * 让"设 value"发生在 option 之后。
         *
         * ⚠️ **坦白**：这一对是**防御性**的 —— 真机上那条"下拉框弹回第一项"
         * 查出来的真因是**读值**（见 `valueOf`：手写的表 + 分组调用点漏传参数），
         * 修完之后**去掉这两行在无头环境里照样绿**（我验过）。留着是因为
         * 浏览器里"value 设在 option 之前"确实不可靠，而代价只有三行。
         * 别把它当成已验证的修复：**能证的那两处是 `valueOf` 与调用点。**
         */
        key: `${options.action}:${String(options.value)}`,
        /*
         * ⚠️ **不要**只写 `value:` 属性。
         *
         * Vue 打补丁的顺序是"先 props、后 children"：`value` 落在一个**还没有
         * option** 的 `<select>` 上等于没设，等 option 插进来时浏览器又把它归到
         * **第一个** option —— 于是下拉框永远显示第一项，用户改完就弹回去。
         *
         * 真机反馈正是这个："编辑器面板属性里也改不了『拉伸』，只能使用『完整放入』"。
         * （`fit` 的三项顺序是 完整放入 / 裁切填满 / 拉伸 —— 所以它永远弹回第一项。）
         *
         * 修法就是上面两行：**`key` 跟着值走（值一变重建）+ `ref` 回调
         * 在 children 之后把 value 设上去**。⚠️ 也试过在 option 上标
         * `selected` —— 那是**冗余**的（去掉它这条断言照样绿，已验证），
         * 所以没有留（少一份"看起来在起作用"的代码）。
         */
        ref: (el: unknown) => {
          const select = el as HTMLSelectElement | null
          if (select) select.value = String(options.value)
        },
        onChange: (ev: Event) => {
          const raw = (ev.target as HTMLSelectElement).value
          const found = options.choices.find((choice) => String(choice.value) === raw)
          options.onPick(found ? found.value : raw)
        },
      },
      options.choices.map((choice) => h('option', { value: String(choice.value) }, choice.label)),
    ),
  ])
}

/**
 * 面板根：统一挂样式 + 根类名。
 *
 * ⚠️ `data-print-panel-kind` 不是给用户看的：**两个面板都叫 `data-print-panel`**
 * （同一个窗口的左右两侧），于是"面板开着吗"这种判断会把左边那个当成右边这个
 * —— 验证脚本里就是这么被骗过一次（以为元素面板开着，实际开的是页面面板，
 * 接着写"X 维度"当然是静默失败）。给个能分辨的标记，省得下次再猜。
 */
function panel(areaId: string, kind: 'page' | 'pages' | 'element' | 'layers', children: (VNode | null)[]): VNode {
  return h(
    'div',
    { class: PRINT_PANEL_CLASS, 'data-print-panel': areaId, 'data-print-panel-kind': kind },
    children,
  )
}

/**
 * **一组字段 = 一张可折叠的卡片**（渲染的是内核的 `NpPanelCard`）。
 *
 * ⚠️ 卡片本身抽在内核里（`components/NpPanelCard.vue`）："一打开面板十几行
 * 全铺在那儿"是所有面板的通病，不是打印包特有的 —— 壳归窗口。
 * 这里只负责"哪几组、叫什么名字、开着还是关着"。
 *
 * ⚠️ 开合状态记在 **`cardState`**（按 `area + 分组 id`）：它属于**视图**
 * （换个窗口看互不影响），所以不进文档；默认值来自参数表的 `expanded`
 * （不写 = 收起，和节点上的段同一条规矩）。
 */
const cardState = reactive(new Map<string, boolean>())

/** 这张卡现在开着吗（用户翻过就听用户的，否则看参数表里的默认值） */
function cardOpen(areaId: string, id: string, defaultOpen: boolean): boolean {
  return cardState.get(`${areaId}:${id}`) ?? defaultOpen
}

function fieldGroup(
  areaId: string,
  id: string,
  label: string,
  hint: string | undefined,
  fields: VNode[],
  defaultOpen = false,
): VNode | null {
  if (fields.length === 0) return null
  return h(
    NpPanelCard,
    {
      label,
      hint,
      /*
       * ⚠️ 分组 id 走 `data-np-card-group`（**不是在组件上写 `data-npp-group`**）：
       * 组件的根元素属性由组件自己渲染，随手塞一个未知 attr 会在
       * `inheritAttrs` 的转发里丢掉 —— 验证脚本读不到它。
       */
      'data-np-card-group': id,
      open: cardOpen(areaId, id, defaultOpen),
      'onUpdate:open': (value: boolean) => cardState.set(`${areaId}:${id}`, value),
    },
    { default: () => fields },
  )
}

/**
 * 一个参数 + 一个值 → 一个字段（**按参数表里的编辑方式分派**）。
 *
 * `bound` 只对「内容」那种输入行有意义：接了线之后内联字面量不是真值，
 * 输入框要禁掉并换标题（否则是个"改了没反应"的骗人控件）。
 */
function fieldFrom(
  param: ParamSpec,
  value: unknown,
  set: (rowId: string, value: unknown) => void,
  bound = false,
  /** 只读（比如"纸张参数还没有文档节点可写"）—— 见 `PrintPagePanel` */
  readonly = false,
): VNode | null {
  const editor = param.editor
  if (readonly) return readonlyField(param, value)
  switch (editor.kind) {
    case 'mm':
    case 'micron':
    case 'number': {
      const numeric = typeof value === 'number' && Number.isFinite(value) ? value : null
      if (numeric === null) return null
      return numberField({
        label: param.label,
        action: param.id,
        value: numeric,
        unit: editor.kind === 'mm' ? 'mm' : editor.kind === 'micron' ? 'µm' : editor.unit,
        min: editor.min,
        max: editor.max,
        step: editor.step ?? (editor.kind === 'micron' ? 10 : 0.5),
        hint: param.hint,
        onInput: (next) => set(param.id, next),
      })
    }
    case 'boolean':
      return toggleField(param.label, param.id, Boolean(value), (next) => set(param.id, next), param.hint)
    case 'enum':
      return enumField({
        label: param.label,
        action: param.id,
        value: typeof value === 'string' || typeof value === 'number' ? value : editor.value,
        choices: editor.options,
        hint: param.hint,
        onPick: (next) => set(param.id, next),
      })
    case 'color':
      return h('label', { class: 'npp-field', title: param.hint ?? param.label }, [
        h('span', { class: 'npp-field__label' }, param.label),
        /*
         * ⚠️ 原生 `input[type=color]` 给的就是 `#rrggbb`，和文档里存的形式一致
         * （不在两端各写一次颜色解析）。旁边那个文本框是给"手输十六进制"用的
         * —— 调色板点不准的时候。
         */
        h('input', {
          type: 'color',
          class: 'npp-input npp-input--color',
          'data-npp-field': `${param.id}__swatch`,
          value: typeof value === 'string' ? value : editor.value,
          onInput: (ev: Event) => set(param.id, (ev.target as HTMLInputElement).value),
        }),
        h('input', {
          type: 'text',
          class: 'npp-input',
          'data-npp-field': param.id,
          value: typeof value === 'string' ? value : editor.value,
          onInput: (ev: Event) => set(param.id, (ev.target as HTMLInputElement).value),
        }),
      ])
    case 'image':
      /*
       * ⚠️ 面板里也渲染**同一个内核控件**（`NpWidgetImage`）：上传 / 粘贴 / 清除
       * 这三个动作在节点上和面板里必须一致 —— 各写一份就会漂移。
       */
      return h(NpWidgetImage, {
        'data-npp-read': String(value),
        /* ⚠️ 面板字段的约定：外层带 `data-npp-field`（和别的字段一致） */
        'data-npp-field': param.id,
        widget: {
          id: param.id,
          kind: 'image' as const,
          value: String(value ?? editor.value ?? ''),
          hint: param.hint,
        },
        onUpdate: (next: unknown) => {
          if (typeof next === 'string') set(param.id, next)
        },
      })
    case 'text':
      return h('div', { class: pcls('group') }, [
        h('div', { class: pcls('title') }, bound ? `${param.label}（由连线提供）` : param.label),
        h('textarea', {
          class: ['npp-input', 'npp-input--area'],
          'data-npp-field': param.id,
          'data-bound': String(bound),
          disabled: bound,
          value: typeof value === 'string' ? value : '',
          onInput: (ev: Event) => set(param.id, (ev.target as HTMLTextAreaElement).value),
        }),
      ])
  }
}

/**
 * **只读字段**：显示值，但不给写入口。
 *
 * ⚠️ 存在的理由：纸张参数归**文档**节点，而单页模式的模板**没有**文档节点 ——
 * 那时写进去会静默失败（`setValue` 找不到那一行就什么都不做）。
 * 与其摆一个"改了没反应"的控件，不如把它显示成只读 + 一句"升级为文档"。
 */
function readonlyField(param: ParamSpec, value: unknown): VNode {
  const editor = param.editor
  const text =
    editor.kind === 'color'
      ? String(value ?? editor.value)
      : typeof value === 'number'
        ? `${Math.round(value * 100) / 100}`
        : String(value ?? '')
  return h(
    'label',
    { class: 'npp-field', 'data-readonly': 'true', title: param.hint ?? param.label },
    [
      h('span', { class: 'npp-field__label' }, param.label),
      h('span', { class: pcls('muted'), 'data-npp-readonly': param.id }, text),
      h('span', { class: 'npp-field__gap' }),
    ],
  )
}

/** 窗口头部的小图标（24×24 stroke，风格和内核那排一致） */
const HEAD_ICONS = {
  edit: 'M4 20h4L19 9l-4-4L4 16zM14 5l4 4',
  preview: 'M2 12s4-6 10-6 10 6 10 6-4 6-10 6-10-6-10-6zM12 15a3 3 0 100-6 3 3 0 000 6z',
  fit: 'M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5',
  actual: 'M4 5h16v14H4zM9 15V9l-2 1M14 9h3l-3 3h3',
} as const

/** 一个图标节点（内核那几个按钮用的是同一种画法） */
function icon(path: string): VNode {
  return h(
    'svg',
    {
      width: 13,
      height: 13,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': 2,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'aria-hidden': 'true',
    },
    [h('path', { d: path })],
  )
}

/* ══════════════════════════════════════════════════════════════════
   左：页面
   ══════════════════════════════════════════════════════════════════ */

export const PrintPagePanel = defineComponent({
  name: 'PrintPagePanel',
  props: {
    area: { type: Object as PropType<EditorHostProps['area']>, required: true },
    doc: { type: Object as PropType<EditorHostProps['doc']>, required: true },
  },
  setup(props) {
    const view = computed(() => viewStateOf(props.area.id))
    const pages = computed(() => pagesOf(props.doc))
    const page = computed(() => activePageOf(pages.value, props.doc.selection))
    const documentNode = computed(() => documentNodeOf(props.doc))

    /**
     * 这份活的模拟结果（一句话）。
     *
     * ⚠️ 它是**派生**的：页面 / 作业参数一变就重算（不缓存、不手动刷新）。
     * 代价是每帧光栅化一遍页面 —— 对标签尺寸（几百点见方）来说可以忽略，
     * 而且只在文档面板打开时才算（面板收起时组件不渲染）。
     */
    const simulationText = computed(() => {
      const docNode = documentNodeOf(props.doc)
      if (!docNode) return '还没有文档节点（先升级为文档）'
      const file = compileToFile(
        pages.value,
        {
          copies: Number(docNode.values.copies),
          cut: docNode.values.cut as PrintJob['cut'],
          darkness: Number(docNode.values.darkness),
          speed: docNode.values.speed as PrintJob['speed'],
          media: docNode.values.media as PrintJob['media'],
          feedMm: Number(docNode.values.feedMm),
        },
      )
      return describeSimulation(simulateJob(file))
    })

    return () => {
      ensurePrintStyles()
      const current = page.value
      const state = view.value
      if (!current) {
        /*
         * ★ 两种情况必须分开说（真机反馈："我断开新建的页面链接到文档，编辑器
         * 还是有那个新建页面" —— 修好之后**编辑器里确实没有了**，但用户会问
         * "我的页面节点去哪了"）：
         *   · 图上**没有**页面节点 → 教他怎么开始；
         *   · 有页面节点、但没接到文档的 `pages.N` 上 → 告诉他接上就回来。
         * ⚠️ 页面节点在节点图上一直在（没丢）—— 编辑器只画**接上的**那些。
         */
        const loosePages = props.doc.nodes.filter((node) => isPageType(node.type)).length
        return panel(props.area.id, 'page', [
          h('div', { class: pcls('title') }, '页面'),
          h(
            'div',
            { class: pcls('empty'), 'data-npp-pages-loose': String(loosePages) },
            loosePages > 0
              ? `图上有 ${loosePages} 个页面节点，但都没接到文档的「页面」行上 —— 接上就会出现在编辑器里`
              : '模板里还没有「热敏打印 · 页面」节点',
          ),
        ])
      }
      /*
       * ⚠️ 纸张参数写到**文档节点**上（参数表里它们归 `document`）。
       * 没接文档时用页面自己的旧值兜底显示，但**写回也写到那一页**上 ——
       * 这样"单页模式"仍然能改纸张（旧模板不会变成只读）。
       */
      const paperTarget = documentNode.value
      const set = (rowId: string, value: unknown) => {
        if (!paperTarget) return
        props.doc.setValue(paperTarget.id, rowId, value, 700)
      }
      /**
       * **升级为文档**：新建文档节点 + 把现有的页按顺序接上去 + 把当前纸张抄过去。
       *
       * ⚠️ 抄纸张这一步不能省：不抄的话这份模板会**当场变形**
       * （页面上的旧值只是只读兜底，新文档会用它自己的默认值 60×30）。
       */
      function upgradeToDocument(): void {
        const list = pages.value
        if (list.length === 0) return
        /* ⚠️ 抄的是**解析后的纸张**（含底色），不是页面上的原始值 */
        const paper = documentSeedFrom(list)
        const id = props.doc.addNode(DOCUMENT_TYPE)
        if (!id) return
        list.forEach((page) => {
          /* ⚠️ 用**槽位号**（`page.index`）而不是数组下标：两者在"空槽"时不同 */
          props.doc.connect(page.id, 'out', id, `pages.${page.index}`)
        })
        props.doc.setValue(id, 'widthMm', paper.widthMm)
        props.doc.setValue(id, 'heightMm', paper.heightMm)
        props.doc.setValue(id, 'dpi', paper.dpi)
        props.doc.setValue(id, 'marginMm', paper.marginMm)
        props.doc.setValue(id, 'backgroundColor', paper.backgroundColor)
        props.doc.select([id])
      }
      const params = paramsFor('document')
      const groups = groupsFor('document', params)
        .map((group) =>
          fieldGroup(
            props.area.id,
            group.id,
            group.label,
            group.hint,
            params
              .filter((param) => param.group === group.id)
              .map((param) =>
                /* ⚠️ 没有文档节点时**只读**：写进去会静默失败（那一行不在页面上） */
                fieldFrom(
                  param,
                  pageValueOf(param.id, current, paperTarget),
                  set,
                  false,
                  !paperTarget,
                ),
              )
              .filter((node): node is VNode => node !== null),
            group.expanded === true,
          ),
        )
        .filter((node): node is VNode => node !== null)

      return panel(props.area.id, 'page', [
        h(
          'div',
          { class: pcls('title'), 'data-npp-document': documentNode.value ? 'bound' : 'none' },
          documentNode.value
            ? `文档 · ${pages.value.length} 页`
            : '单页模式：纸张只读',
        ),
        /*
         * ★ **这份活怎么打**（一句话）—— 份数 / 切刀 / 浓度那些参数改完，
         * 用户要能一眼看到结果（而不是去三个下拉框里拼）。
         */
        /*
         * ★ **这份活会做什么**（模拟出来的）：打几张 / 切几刀 / 走多长纸 / 上墨多少。
         *
         * ⚠️ 走的是**同一个**参考光栅器与动作脚本（S4 + `jobScript.ts`）——
         * 面板上这个数与"打出来是什么样"同源，不会各说各话。
         * 它回答的是用户真会问的问题："一卷纸能打几份"。
         */
        h(
          'div',
          { class: pcls('muted'), 'data-npp-simulate': simulationText.value },
          simulationText.value,
        ),
        h(
          'div',
          {
            class: pcls('muted'),
            'data-npp-job': describeJob(
              normalizeJob({
                copies: Number(documentNode.value?.values.copies),
                cut: documentNode.value?.values.cut as PrintJob['cut'],
                darkness: Number(documentNode.value?.values.darkness),
                speed: documentNode.value?.values.speed as PrintJob['speed'],
                media: documentNode.value?.values.media as PrintJob['media'],
                feedMm: Number(documentNode.value?.values.feedMm),
              }),
            ),
          },
          describeJob(
            normalizeJob({
              copies: Number(documentNode.value?.values.copies),
              cut: documentNode.value?.values.cut as PrintJob['cut'],
              darkness: Number(documentNode.value?.values.darkness),
              speed: documentNode.value?.values.speed as PrintJob['speed'],
              media: documentNode.value?.values.media as PrintJob['media'],
              feedMm: Number(documentNode.value?.values.feedMm),
            }),
          ),
        ),
        /* ⚠️ 单页模式下的**升级入口**：没有它，这份模板连纸张都改不了 */
        documentNode.value
          ? null
          : h(
              'div',
              { class: pcls('group') },
              [
                h(
                  'button',
                  {
                    class: 'npp-btn',
                    type: 'button',
                    'data-npp-field': 'upgrade-document',
                    title: '新建「打印 · 文档」节点，把现有的页接上去，并把当前的纸张抄过去',
                    onClick: upgradeToDocument,
                  },
                  '升级为文档',
                ),
                h('div', { class: pcls('muted') }, '新建文档节点并接上现有页面'),
              ],
            ),
        ...groups,
        fieldGroup(props.area.id, 'view', '视图', '这些是**窗口**的状态，不进文档（换个窗口看互不影响）', [
          enumField({
            label: '网格',
            action: 'grid',
            value: state.gridMm,
            choices: GRID_CHOICES.map((mm) => ({ label: mm === 0 ? '关' : `${mm} mm`, value: mm })),
            onPick: (next) => {
              state.gridMm = Number(next)
            },
          }),
          toggleField('吸附', 'snap', state.snap, (next) => {
            state.snap = next
          }),
          toggleField('标尺', 'rulers', state.rulers, (next) => {
            state.rulers = next
          }),
          /*
           * ⚠️ 这里**没有缩放按钮**：缩放属于"看哪儿"，放在**窗口头部**
           * （真机反馈："下面的区域里，缩放相关的也可以去掉了"）。
           * 这个卡片只管"摆东西时的规矩"：网格 / 吸附 / 标尺。
           */
        ]),
      ])
    }
  },
})

/** 页面节点的参数值（从**算好的**页面视图读，不再去翻原值） */
/**
 * 纸张 / 作业参数现在的值。
 *
 * ⚠️ 导出理由同上（验证脚本要用）。
 *
 * ⚠️ **先读文档节点上的原始值**（有文档时它才是"用户填的那个"），
 * 没有再退回页面视图里**解析后**的那一份（单页模式的兜底，见
 * `resolvedPaperOf`）。
 *
 * ⚠️ 这里漏过一项就会表现成"改了没反应"：`backgroundColor` 曾经不在
 * 这张表里，于是文档里明明改成了深色，文档面板的「纸张」分组里
 * 还是白的（真机反馈）。**加纸张参数时这里必须跟着加** ——
 * 下面那条 `fallback` 分支就是为此留的（视图上没有的项去文档节点上取）。
 */
export function pageValueOf(rowId: string, page: PageView, documentNode?: EditorNodeView | null): unknown {
  const raw = documentNode?.values[rowId]
  if (raw !== undefined) return raw
  /* 页面视图上的纸张已经是"解析后"的那一份（文档优先，见 `resolvedPaperOf`） */
  if (rowId === 'widthMm') return page.widthMm
  if (rowId === 'heightMm') return page.heightMm
  if (rowId === 'marginMm') return page.marginMm
  if (rowId === 'dpi') return page.dpi
  if (rowId === 'backgroundColor') return page.backgroundColor
  return undefined
}

/* ══════════════════════════════════════════════════════════════════
   左：页面（多页管理的第二页）
   ══════════════════════════════════════════════════════════════════ */

/**
 * **页面板**：这一份文档有哪些页 —— 加一页 / 删一页 / 切到某一页。
 *
 * ⚠️ 页序 = `pages.N` 的序号（和组件的层序同一条规矩）。所以"上移/下移"
 * 就是改两条边的句柄序号，走 `doc.reorderPages()`（宿主那边统一处理）。
 */
export const PrintPagesPanel = defineComponent({
  name: 'PrintPagesPanel',
  props: {
    area: { type: Object as PropType<EditorHostProps['area']>, required: true },
    doc: { type: Object as PropType<EditorHostProps['doc']>, required: true },
  },
  setup(props) {
    const pages = computed(() => pagesOf(props.doc))
    const active = computed(() => activePageOf(pages.value, props.doc.selection))

    /**
     * 加一页：接到文档上，**没有文档就先建一个**。
     *
     * ⚠️ 真机反馈："点击添加页面之后，它只添加了页面，没有添加文档节点，
     * 正常应该同时添加文档节点和一个页面节点"。纸张参数归文档，所以只有
     * 页面节点的模板**什么都改不了**（面板里全是只读的）。
     */
    function addPage(): void {
      const documentNode = documentNodeOf(props.doc) ?? null
      const docId = documentNode?.id ?? props.doc.addNode(DOCUMENT_TYPE)
      const id = props.doc.addNode(PAGE_TYPE)
      if (!id) return
      if (docId) {
        /*
         * ⚠️ 接在**下一个空槽**上（不是"页数"那一位）：页码不压实（见
         * `orderedPages`），按页数算会把新页接到已占的槽位上、把原来那页顶掉。
         */
        props.doc.connect(id, 'out', docId, nextPagesHandle(props.doc.links, docId))
      }
      props.doc.select([id])
    }



    return () => {
      ensurePrintStyles()
      const list = pages.value
      const documentNode = documentNodeOf(props.doc)
      const children: (VNode | null)[] = [
        h('div', { class: pcls('title'), 'data-npp-pages': String(list.length) }, `页面（${list.length}）`),
      ]
      if (!documentNode) {
        children.push(
          h('div', { class: pcls('empty') }, '单页模式'),
        )
      } else if (list.length === 0) {
        /* ⚠️ 有文档、但 `pages.N` 上一条边都没有 —— 说明"为什么一页都没有" */
        children.push(
          h(
            'div',
            { class: pcls('empty'), 'data-npp-pages-empty': 'true' },
            '文档的「页面」行上还没有接页面节点 —— 接上就会出现在这里，也会出现在编辑器里',
          ),
        )
      }
      /*
       * ★ 一行 = **文档 `pages.N` 上的一张纸**（不是"一个页面节点"）：
       * 同一个页面节点接两个槽位时这里有**两行**（物理上就是同一页印两次）——
       * 行标识用 `page.key`（`${节点id}#${槽位}`），不是 `page.id`（那个会重）。
       * ⚠️ 显示的序号是**槽位号**：中间空一格时第 3 页就显示"3"，不压实。
       */
      list.forEach((page, index) => {
        children.push(
          h(
            'div',
            {
              class: [pcls('row'), 'npp-row--clickable'],
              'data-npp-page-row': page.key,
              'data-npp-page-index': String(page.index),
              'data-active': String(active.value?.key === page.key),
              onClick: () => props.doc.select([page.id]),
            },
            [
              h('span', null, `${page.index + 1}. ${page.title}`),
              h('span', { class: pcls('muted') }, `${page.elements.length} 个组件`),
              h('span', { class: 'npp-field__gap' }),
              h(
                'button',
                {
                  class: 'npp-btn',
                  type: 'button',
                  'data-npp-field': `page-up:${page.key}`,
                  disabled: index === 0,
                  onClick: (ev: Event) => {
                    ev.stopPropagation()
                    props.doc.reorderChildren(documentNode?.id ?? '', 'pages', page.id, -1)
                  },
                },
                '↑',
              ),
              h(
                'button',
                {
                  class: 'npp-btn',
                  type: 'button',
                  'data-npp-field': `page-down:${page.key}`,
                  disabled: index === list.length - 1,
                  onClick: (ev: Event) => {
                    ev.stopPropagation()
                    props.doc.reorderChildren(documentNode?.id ?? '', 'pages', page.id, 1)
                  },
                },
                '↓',
              ),
              h(
                'button',
                {
                  class: ['npp-btn', 'npp-btn--danger'],
                  type: 'button',
                  'data-npp-field': `page-del:${page.key}`,
                  title: '删掉这个页面节点（同一个节点接了多页时，那些页会一起没）',
                  onClick: (ev: Event) => {
                    ev.stopPropagation()
                    /*
                     * ⚠️ 删的是**节点**（不是"这一条连线"）：`removeNodes` 走宿主
                     * 的级联删除 —— 同一个节点接了 `pages.0` / `pages.1` 时，
                     * 两页会一起没。按实例删要新增一条"断开这一路"的写入口，
                     * 那是宿主的能力（这一轮不做，写在这里免得当成 bug）。
                     */
                    props.doc.removeNodes([page.id])
                  },
                },
                '✕',
              ),
            ],
          ),
        )
      })
      children.push(
        h(
          'button',
          {
            class: 'npp-btn',
            type: 'button',
            'data-npp-field': 'page-add',
            onClick: addPage,
          },
          '+ 新页面',
        ),
      )
      return panel(props.area.id, 'pages', children)
    }
  },
})

/* ══════════════════════════════════════════════════════════════════
   右：图层（按层序管理的第二页）
   ══════════════════════════════════════════════════════════════════ */

/**
 * **图层面板**：当前页上有哪些组件（层序 = `items.N`）。
 *
 * ⚠️ 层序**就是**数组顺序（IR 是有序指令表，后画的压在上面），
 * 所以这里的上移 / 下移改的是 `items.N`（走 `doc.reorderItems()`），
 * 而不是某个"z-index 参数" —— 那种参数迟早和绘制顺序对不上。
 */
export const PrintLayersPanel = defineComponent({
  name: 'PrintLayersPanel',
  props: {
    area: { type: Object as PropType<EditorHostProps['area']>, required: true },
    doc: { type: Object as PropType<EditorHostProps['doc']>, required: true },
  },
  setup(props) {
    const pages = computed(() => pagesOf(props.doc))
    const page = computed(() => activePageOf(pages.value, props.doc.selection))
    const selected = computed(() => new Set(props.doc.selection))

    return () => {
      ensurePrintStyles()
      const current = page.value
      if (!current) {
        return panel(props.area.id, 'layers', [h('div', { class: pcls('empty') }, '还没有页面')])
      }
      /* ⚠️ 图层面板按**从下到上**列（数组顺序 = 绘制顺序），但显示时把最上层放最前 —— 和用户的直觉一致 */
      const layers = [...current.elements].reverse()
      return panel(props.area.id, 'layers', [
        h('div', { class: pcls('title'), 'data-npp-layers': String(layers.length) }, `图层（${layers.length}）· 上层在前`),
        ...layers.map((element, indexFromTop) => {
          const index = current.elements.length - 1 - indexFromTop
          return h(
            'div',
            {
              class: [pcls('row'), 'npp-row--clickable'],
              'data-npp-layer': element.id,
              'data-npp-layer-index': String(index),
              'data-active': String(selected.value.has(element.id)),
              onClick: () => props.doc.select([element.id]),
            },
            [
              h('span', null, element.title),
              h('span', { class: pcls('muted') }, element.kind),
              h('span', { class: 'npp-field__gap' }),
              h(
                'button',
                {
                  class: 'npp-btn',
                  type: 'button',
                  'data-npp-field': `layer-up:${element.id}`,
                  disabled: index === current.elements.length - 1,
                  onClick: (ev: Event) => {
                    ev.stopPropagation()
                    /* "上移" = 往数组后面挪（后画的在上面） */
                    props.doc.reorderChildren(current.id, 'items', element.id, 1)
                  },
                },
                '↑',
              ),
              h(
                'button',
                {
                  class: 'npp-btn',
                  type: 'button',
                  'data-npp-field': `layer-down:${element.id}`,
                  disabled: index === 0,
                  onClick: (ev: Event) => {
                    ev.stopPropagation()
                    props.doc.reorderChildren(current.id, 'items', element.id, -1)
                  },
                },
                '↓',
              ),
            ],
          )
        }),
      ])
    }
  },
})

/* ══════════════════════════════════════════════════════════════════
   右：元素
   ══════════════════════════════════════════════════════════════════ */

/** 选中节点 → 它在页面上的视图（不在任何页面上 → null） */
function selectedElement(pages: readonly PageView[], selection: readonly string[]): ElementView | null {
  for (const id of selection) {
    for (const page of pages) {
      const found = page.elements.find((el) => el.id === id)
      if (found) return found
    }
  }
  return null
}

/** 元素 → 参数表的组件种类（二维码是独立的一种，虽然渲染路径和条码一样） */
function componentOf(element: ElementView): ParamComponent {
  if (element.kind === 'barcode') return element.barcode?.square ? 'qrcode' : 'barcode'
  return element.kind
}

/**
 * **面板要显示的那个值** —— 一律从 `ElementView`（已经算好的视图模型）读。
 *
 * ⚠️ 不去翻 `doc` 的原值：条码的宽度 / 静区 / 模块宽都是**算出来的**，
 * 面板必须显示真实值（用户填 375µm、实际是 3 个点 = 0.375mm，
 * 这两个数不是一回事 —— 显示"点了哪一步"才有用）。
 */
/**
 * 面板上某个参数**现在的值**。
 *
 * ⚠️ 导出是**给验证脚本用的**：`verify:print` 有一条"每个参数都读得到"的
 * 遍历断言 —— 这正是能防住"手写表腐烂"的那道闸（见文件里那段教训）。
 *
 * ## ★ 为什么必须"通用地"读，而不是列一张表
 *
 * 这里原来是一张手写的表（只列了十来个 id），**没列到的就返回 `undefined`** ——
 * 而 `fieldFrom` 拿到 `undefined` 会退回参数**默认值**，于是那些控件显示的是
 * 默认值、不是真值：选「拉伸」它弹回「完整放入」、颜色永远显示黑、
 * 不透明度永远 1……（真机反馈："面板属性里也改不了『拉伸』，只能使用『完整放入』"。）
 *
 * 表是会**腐烂**的：每加一个参数就得记得回来补一行，忘了不报错、只是界面上
 * 悄悄显示错值。所以顺序改成：
 *
 *   ① 元素视图（`ElementView`）—— 布局期**算出来的**那几个（xMm / 条高 / 模块宽…）；
 *   ② **节点上的字面量**（用户真正填的那个）—— 覆盖所有参数，不需要维护；
 *   ③ 条码视图里的几何兜底。
 *
 * ⚠️ 求值结果（连线过来的值）在视图那一层已经并进来了（`evaluatedComponent`）：
 * 这里读的是"界面上该显示什么"，和节点上那个控件读的是同一份数据。
 */
export function valueOf(
  rowId: string,
  element: ElementView,
  /**
   * 这个节点的**求值结果**（端口值）—— 面板该显示"实际是什么"，
   * 而不是"字面量写了什么"（连线过来时两者不同）。
   *
   * ⚠️ 它同时也是**响应式的来源**：节点模型里的控件值是普通对象上的字段，
   * 不是响应式的 —— 只读字面量的话，面板算完一次就不再重算
   * （真机反馈"面板里改不了拉伸、只能使用完整放入"就是这么来的：
   * 下拉框停在第一次读到的那个值上）。
   * 求值结果会随图的变化而更新，所以读它 = 面板跟着变。
   */
  evaluated?: ReadonlyMap<string, unknown> | null,
  /** 节点上的字面量（最后兜底：求值失败 / 还没算出来时） */
  node?: EditorNodeView | null,
): unknown {
  const direct: Record<string, unknown> = {
    xMm: element.box.xMm,
    yMm: element.box.yMm,
    wMm: element.box.wMm,
    hMm: element.box.hMm,
    text: element.text,
    sizeMm: element.sizeMm,
    align: element.align,
    lineWidthMm: element.lineWidthMm,
    fill: element.fill,
    radiusMm: element.radiusMm,
    invert: element.invert,
    bold: element.bold,
    vAlign: element.vAlign,
    lineHeightMm: element.lineHeightMm,
    rotationDeg: element.rotationDeg,
  }
  if (rowId in direct) return direct[rowId]
  /*
   * ② **求值结果**里的 `props`（组件值的参数都在那儿；连线过来的真值也在这儿）。
   *
   * ⚠️ 路径是 `outputs.get(节点).get('out').props[rowId]` —— 直接
   * `outputs.get(节点).get(rowId)` 是**查不到的**（那一层是端口 id）：
   * 第一版就这么写，于是这一支永远命中不了、静默退回字面量。
   * ⚠️ 这一支也是**响应式的来源**：节点模型上的控件值是普通对象的字段，
   * 不是响应式的；求值结果每次重算都是新的 Map，所以读它才会跟着更新
   * （"面板里改不了拉伸"的另一半原因）。
   */
  const out = evaluated?.get('out')
  const props = out && typeof out === 'object' ? (out as { props?: unknown }).props : undefined
  const computed =
    props && typeof props === 'object' ? (props as Record<string, unknown>)[rowId] : undefined
  if (computed !== undefined) return computed
  /*
   * ③ **节点上的字面量**（通用兜底 —— 新加参数不需要回来改这里）。
   * ⚠️ 放在视图之后：几何那几项以视图为准（拖动会改它们，视图才是"现在"）。
   */
  const literal = node?.values[rowId]
  if (literal !== undefined) return literal
  const bc = element.barcode
  if (!bc) return undefined
  const fromBarcode: Record<string, unknown> = {
    symbology: bc.symbology,
    xDimMicron: bc.xDimMicron,
    heightMm: bc.barHeightMm,
    showHri: bc.showHri,
    ecLevel: bc.ecLevel,
  }
  return fromBarcode[rowId]
}

export const PrintElementPanel = defineComponent({
  name: 'PrintElementPanel',
  props: {
    area: { type: Object as PropType<EditorHostProps['area']>, required: true },
    doc: { type: Object as PropType<EditorHostProps['doc']>, required: true },
  },
  setup(props) {
    const pages = computed(() => pagesOf(props.doc))
    const element = computed(() => selectedElement(pages.value, props.doc.selection))
    const orphans = computed(() => orphanComponents(props.doc))
    const page = computed(() => activePageOf(pages.value, props.doc.selection))

    /** 把一个游离组件接到当前页 */
    function wire(node: EditorNodeView): void {
      const target = page.value
      if (!target) return
      props.doc.connect(node.id, 'out', target.id, nextItemsHandle(props.doc.links, target.id))
    }

    return () => {
      ensurePrintStyles()
      const current = element.value
      const children: (VNode | null)[] = [h('div', { class: pcls('title') }, '元素')]

      if (!current) {
        children.push(h('div', { class: pcls('empty') }, '没有选中组件'))
      } else {
        const set = (rowId: string, value: unknown) => props.doc.setValue(current.id, rowId, value, 700)
        const bound = props.doc.links.some(
          (link) => link.target === current.id && link.targetHandle === 'text',
        )
        const component = componentOf(current)
        const params = paramsFor(component, {
          symbologies: SYMBOLOGIES.filter((spec) => spec.kind === (component === 'qrcode' ? '2d' : '1d')),
          /* 面板显示的永远是文档里的值；defaults 只影响"新节点放下来时是什么" */
          defaults: component === 'qrcode' ? QRCODE_DEFAULTS : component === 'barcode' ? BARCODE_DEFAULTS : undefined,
        })
        /*
         * ⚠️ **重点参数（X / Y / 宽 / 高 / 旋转）要单独先渲染**：
         * 它们 `group: null`（不进折叠段），只遍历分组的话它们就整块消失了 ——
         * 表现是"面板里没有位置输入框"（加节点拖动都还在，只是改不了数字）。
         */
        children.push(
          fieldGroup(
            props.area.id,
            /* ⚠️ 重点参数那张卡**永远展开**：它们是"每次都要看"的那几行 */
            'key',
            '位置与尺寸',
            'X / Y / 宽 / 高 / 旋转 —— 这是每次都要改的，所以不进折叠卡',
            keyParamsFor(params)
              .map((param) =>
                fieldFrom(
                  param,
                  valueOf(
                    param.id,
                    current,
                    props.doc.outputs.get(current.id),
                    props.doc.nodes.find((n) => n.id === current.id),
                  ),
                  set,
                ),
              )
              .filter((node): node is VNode => node !== null),
            true,
          ),
        )
        for (const group of groupsFor(component, params)) {
          children.push(
            fieldGroup(
              props.area.id,
              group.id,
              group.label,
              group.hint,
              params
                .filter((param) => param.group === group.id)
                .map((param) =>
                  fieldFrom(
                    param,
                    /*
                     * ⚠️ **四个参数都要传**：只传 rowId + element 的话，"求值结果"与
                     * "节点字面量"两条路都拿不到 —— 于是分组里的参数（`fit`、
                     * `color`、`threshold`…）永远显示**参数默认值**，用户改了立刻弹回去
                     * （真机反馈"面板里改不了拉伸"的最后一层原因：这条调用点漏改了）。
                     */
                    valueOf(
                      param.id,
                      current,
                      props.doc.outputs.get(current.id),
                      props.doc.nodes.find((n) => n.id === current.id),
                    ),
                    set,
                    bound,
                  ),
                )
                .filter((node): node is VNode => node !== null),
              group.expanded === true,
            ),
          )
        }
        /* 条码 / 二维码：算出来的真实值（只读）+ 两句提醒 */
        if (current.barcode) children.push(...barcodeReadout(current))
        children.push(
          fieldGroup(props.area.id, 'actions', '操作', undefined, [
            h(
              'button',
              {
                class: ['npp-btn', 'npp-btn--danger'],
                type: 'button',
                'data-npp-field': 'el-delete',
                /* 删节点走宿主的删除路径（级联边 / 受保护节点都在那里） */
                onClick: () => props.doc.removeNodes([current.id]),
              },
              '删除这个组件',
            ),
          ]),
        )
      }

      /*
       * ⚠️ 游离组件那一栏不是调试信息：节点图里删掉一条边之后组件就从纸上
       * "消失"了，而用户只会以为编辑器丢了东西。列出来 + 一键接回去，
       * 缺的那一步就补上了。
       */
      if (orphans.value.length > 0) {
        children.push(
          h('div', { class: pcls('group'), 'data-npp-orphans': String(orphans.value.length) }, [
            h('div', { class: pcls('title') }, `未接到页面上的组件（${orphans.value.length}）`),
            ...orphans.value.map((node) =>
              h('div', { class: pcls('row'), 'data-npp-orphan': node.id, onClick: () => props.doc.select([node.id]) }, [
                h('span', null, node.title),
                h('span', { class: pcls('muted') }, node.id),
                h('span', { class: 'npp-field__gap' }),
                h(
                  'button',
                  {
                    class: 'npp-btn',
                    type: 'button',
                    'data-npp-field': `wire:${node.id}`,
                    onClick: (ev: Event) => {
                      ev.stopPropagation()
                      wire(node)
                    },
                  },
                  '接到本页',
                ),
              ]),
            ),
          ]),
        )
      }

      return panel(props.area.id, 'element', children)
    }
  },
})

/**
 * **算出来的真实值**（只读）+ 两句提醒。
 *
 * ⚠️ 少这一栏，用户就只能靠打印出来量：他填的 375µm 在 203dpi 下
 * 实际是 3 个点 = 0.375mm，而"条宽几点"才是决定能不能扫出来的那个数。
 */
function barcodeReadout(current: ElementView): VNode[] {
  const view = current.barcode
  if (!view) return []
  const rows: [string, string][] = [
    ['符号', view.label],
    [
      '模块宽',
      `${view.moduleDots} 点 = ${view.moduleMm.toFixed(3)}mm` +
        (view.moduleFromWidth ? '（按宽度反推）' : '（按 X 维度）'),
    ],
  ]
  if (view.encoded?.kind === '1d') {
    rows.push(['条数', `${view.encoded.bars.length}`], ['内容宽', `${view.encoded.contentModules} 模块`])
  } else if (view.encoded?.kind === '2d') {
    rows.push(['矩阵', `${view.encoded.cols}×${view.encoded.rows} 模块`])
  }
  if (!view.square) rows.push(['条高', `${view.barHeightMm}mm（总高含 HRI ${view.hriMm}mm）`])
  rows.push(['静区', `${(view.quietZoneMm * 2).toFixed(2)}mm（两侧）`])
  /*
   * ⚠️ 有框时**成品宽比"内容 + 静区"大**：框不许压静区，所以内容整体内缩了
   * 一条线宽、而组件框把让出来的那一圈算进自己的占地（见
   * `barcodeElementOf` 的 `insetMm`）。这一行是为了让那个 0.5mm 说得清 ——
   * 不然用户会以为"加了个框，条码自己变宽了"。
   */
  if (view.insetMm > 0) {
    rows.push([
      '框内缩',
      `${(view.insetMm * 2).toFixed(2)}mm（两侧，框线宽 ${current.lineWidthMm}mm）`,
    ])
  }
  rows.push(['成品宽', `${current.box.wMm}mm`])
  const out: VNode[] = [
    h('div', { class: pcls('group'), 'data-npp-readout': 'barcode' }, [
      h('div', { class: pcls('title') }, '实际值'),
      ...rows.map(([label, value]) =>
        h('div', { class: pcls('row'), 'data-npp-readout-row': label }, [
          h('span', { class: pcls('muted') }, label),
          h('span', { class: 'npp-field__gap' }),
          h('span', null, value),
        ]),
      ),
    ]),
  ]
  if (view.error) {
    out.push(h('div', { class: pcls('empty'), 'data-npp-barcode-msg': 'error' }, view.error))
  } else if (view.xDimWarning) {
    out.push(h('div', { class: pcls('empty'), 'data-npp-barcode-msg': 'warn' }, view.xDimWarning))
  }
  return out
}

/* ══════════════════════════════════════════════════════════════════
   窗口头部：视图控件（缩放 / 编辑-预览 / 导出）
   ══════════════════════════════════════════════════════════════════ */

/**
 * **窗口头部的控件**（内核的 `EditorContribution.header` 能力）。
 *
 * ⚠️ 为什么从这里放而不是留在正文工具栏：工具栏是"**往纸上加东西**"
 * （文本 / 矩形 / 条码…），而缩放与视图切换是"**看哪儿**" ——
 * 两者混在一排，真机反馈就是"按钮太多、分不清"。
 *
 * ⚠️ 它只拿得到 `area`：视图状态按 `area.id` 读（`viewStateOf`），
 * 所以分屏之后每个窗口各有一份，互不打架。
 */
export const PrintHeaderControls = defineComponent({
  name: 'PrintHeaderControls',
  props: {
    area: { type: Object as PropType<EditorHostProps['area']>, required: true },
    doc: { type: Object as PropType<EditorHostProps['doc']>, required: true },
  },
  setup(props) {
    const view = computed(() => viewStateOf(props.area.id))
    return () => {
      const state = view.value
      /*
       * ★ **编辑 / 预览是一个开关按钮的两种状态**：默认**激活 = 编辑**，
       * 取消激活 = 预览（真机反馈："编辑按钮默认是激活状态，取消激活就是预览"）。
       *
       * ⚠️ 图标按钮（真机反馈："顶部的『编辑 适应 1:1』等按钮没有做成图标的"）——
       * 文字留给 `title` 与读屏。
       */
      const editing = state.mode === 'edit'
      /*
       * ⚠️ `np-area__head-group` 是**内核给的**类（`theme/np-strip.css`）：
       * 横排、不换行、不收缩 —— 包贡献的头部控件不许因为窗口变窄就叠成竖排
       * （真机反馈："这三个按钮在宽度不够的时候，会从左右排序变成上下排序"）。
       */
      return h(
        'span',
        { class: 'npp-head np-area__head-group', 'data-print-header': props.area.id },
        [
        h(
          'button',
          {
            class: 'np-area__icon',
            type: 'button',
            'data-npp-action': 'mode-toggle',
            'data-active': String(editing),
            'aria-pressed': editing,
            title: editing ? '编辑中（点一下切到预览）' : '预览中（点一下回到编辑）',
            'aria-label': editing ? '编辑' : '预览',
            onClick: () => {
              state.mode = editing ? 'preview' : 'edit'
            },
          },
          icon(editing ? HEAD_ICONS.edit : HEAD_ICONS.preview),
        ),
        h('span', { class: 'npp-head__sep' }),
        h(
          'button',
          {
            class: 'np-area__icon',
            type: 'button',
            'data-npp-action': 'zoom-fit',
            /*
             * ⚠️ **不带 `data-active`**：它是一个"做一件事"的按钮（把视图设成适应），
             * 不是开关。真机反馈："适应缩放和 1:1 按钮…不应该有选中状态"——
             * 带上选中态会让用户以为"再点一下会取消适应"。
             * 选中态只给**开关**（编辑 / 预览、区域开关、侧栏开关）。
             */
            title: '适应窗口',
            'aria-label': '适应窗口',
            onClick: () => {
              state.zoom = 'fit'
            },
          },
          icon(HEAD_ICONS.fit),
        ),
        h(
          'button',
          {
            class: 'np-area__icon',
            type: 'button',
            'data-npp-action': 'zoom-actual',
            /* ⚠️ 同上：纯按钮，没有选中态 */
            title: '物理尺寸 1:1',
            'aria-label': '物理尺寸 1:1',
            onClick: () => {
              state.zoom = 1
            },
          },
          icon(HEAD_ICONS.actual),
        ),
        h('span', { class: 'npp-head__sep' }),
        h(
          'span',
          {
            class: 'npp-head__zoom',
            'data-npp-zoom': String(Math.round((state.zoom === 'fit' ? 0 : state.zoom) * 1000) / 1000),
          },
            state.zoom === 'fit' ? '适应' : `${Math.round((state.zoom as number) * 100)}%`,
          ),
        ],
      )
    }
  },
})
